import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/apiHelper';
import { formatPrice } from '../../utils/formatHelper';

import { useDataTable } from '../hooks/useDataTable';
import DataTable from '../components/shared/DataTable';
import PageHeader from '../components/shared/PageHeader';
import StatusBadge from '../components/shared/StatusBadge';
import FilterBar from '../components/shared/FilterBar';
import ConfirmModal from '../components/shared/ConfirmModal';
import placeholderImage from '../../assets/images/placeholder.jpg';
import ShopifyImportModal from './ShopifyImportModal';
import DiscountModal from './DiscountModal';
import ProductModal from './ProductModal';
import ActionDrawer from '../components/shared/ActionDrawer';
import { useModal } from '../hooks/useModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showSuccess, showError, showServerError } from '../../utils/toastHelper';

function ProductManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialStockFilter = urlParams.get('stockFilter') || '';
  const {
    isOpen: isShopifyOpen,
    open: openShopify,
    close: closeShopify
  } = useModal();
  const {
    isOpen: isDiscountOpen,
    open: openDiscount,
    close: closeDiscount
  } = useModal();
  const {
    isOpen: isProductDrawerOpen,
    open: openProductDrawer,
    close: closeProductDrawer
  } = useModal();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Custom filters for useDataTable hooks
  const {
    data: products,
    loading,
    pagination,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleFilterChange,
    filters,
    refresh
  } = useDataTable('/products', {
    initialFilters: {
      search: '',
      stockFilter: initialStockFilter
    }
  });

  const handleEdit = async (product) => {
    try {
      const response = await apiRequest(`/products/${product.id}`);
      if (response.success) {
        setSelectedProduct(response.product || response.data);
        openProductDrawer();
      } else {
        showError('Ürün verileri yüklenirken hata oluştu');
      }
    } catch (error) {
      console.error(error);
      showError('Ürün verileri yüklenirken hata oluştu');
    }
  };

  const handleCreate = () => {
    setSelectedProduct(null);
    openProductDrawer();
  };

  const handleCloseProductDrawer = (shouldRefresh = false) => {
    closeProductDrawer();
    setSelectedProduct(null);
    if (shouldRefresh) {
      refresh();
    }
  };

  const handleDelete = (productId) => {
    setProductToDelete(productId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    try {
      setDeleteLoading(true);
      await apiRequest(`/products/${productToDelete}`, { method: 'DELETE' });
      showSuccess('Ürün başarıyla silindi');
      setShowDeleteConfirm(false);
      setSelectedProductIds(prev => prev.filter(id => id !== productToDelete));
      setProductToDelete(null);
      refresh();
    } catch (error) {
      console.error(error);
      showError('Ürün silinirken bir hata oluştu');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedProductIds.length === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await apiRequest('/products/bulk-delete', {
        method: 'DELETE',
        data: { productIds: selectedProductIds }
      });

      if (response.success) {
        showSuccess(`${selectedProductIds.length} ürün silindi`);
        setSelectedProductIds([]);
        setShowBulkDeleteConfirm(false);
        refresh();
      } else {
        showError(response.message);
      }
    } catch (error) {
      console.error(error);
      showServerError();
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClearFilters = () => {
    handleFilterChange('search', '');
    handleFilterChange('stockFilter', '');
    // Clear URL params too
    if (location.search) navigate('/admin/products', { replace: true });
  };

  const handleSelectionChange = (product) => {
    setSelectedProductIds(prev => {
      if (prev.includes(product.id)) {
        return prev.filter(id => id !== product.id);
      } else {
        return [...prev, product.id];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProductIds(products.map(p => p.id));
    } else {
      setSelectedProductIds([]);
    }
  };

  // Columns for List View
  const columns = [
    {
      header: (
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            checked={products.length > 0 && selectedProductIds.length === products.length}
            onChange={handleSelectAll}
          />
        </div>
      ),
      accessor: 'id',
      width: '50px',
      render: (row) => (
        <div className="form-check" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedProductIds.includes(row.id)}
            onChange={() => handleSelectionChange(row)}
          />
        </div>
      )
    },
    {
      header: 'Ürün',
      accessor: 'name',
      render: (row) => (
        <div className="d-flex align-items-center">
          <img
            src={row.mainImage || placeholderImage}
            alt={row.name}
            className="rounded border"
            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
          />
          <div className="ms-3">
            <div className="fw-bold text-dark">{row.name}</div>
            {row.discountedPrice && (
              <div className="badge bg-soft-success text-success small">
                <i className="fas fa-tag me-1"></i> İndirimli
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      header: 'Fiyat',
      accessor: 'price',
      render: (row) => (
        <div>
          {row.discountedPrice ? (
            <>
              <div className="fw-bold text-success">{formatPrice(row.discountedPrice)}</div>
              <div className="text-decoration-line-through text-muted small">{formatPrice(row.price)}</div>
            </>
          ) : (
            <div className="fw-bold text-dark">{formatPrice(row.price)}</div>
          )}
        </div>
      )
    },
    {
      header: 'Stok',
      accessor: 'stock',
      render: (row) => (
        <div className={row.stock > 0 ? 'text-success' : 'text-danger'}>
          <i className={`fas fa-${row.stock > 0 ? 'check' : 'times'}-circle me-1`}></i>
          {row.stock > 0 ? `${row.stock} adet` : 'Stok Yok'}
        </div>
      )
    },
    {
      header: 'Durum',
      accessor: 'status',
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'İşlemler',
      accessor: 'actions',
      render: (row) => (
        <div className="dropdown" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-light text-muted" data-bs-toggle="dropdown">
            <i className="fas fa-ellipsis-v"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
            <li>
              <button className="dropdown-item" onClick={() => handleEdit(row)}>
                <i className="fas fa-edit fa-fw me-2"></i> Düzenle
              </button>
            </li>
            <li>
              <button className="dropdown-item text-danger" onClick={() => handleDelete(row.id)}>
                <i className="fas fa-trash fa-fw me-2"></i> Sil
              </button>
            </li>
          </ul>
        </div>
      )
    }
  ];

  // Show full page loader on initial load
  if (loading && (!products || products.length === 0)) {
    return (
      <div className="container-fluid p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Ürünler yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <PageHeader
        title="Ürün Yönetimi"
        description="Mağaza ürünlerini buradan yönetebilirsiniz."
        breadcrumbs={[{ label: 'Admin' }, { label: 'Ürünler' }]}
        actions={
          <div className="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
            <button className="btn btn-white border" onClick={openShopify}>
              <i className="fab fa-shopify me-2"></i> İçe Aktar
            </button>
            <button className="btn btn-primary" onClick={handleCreate}>
              <i className="fas fa-plus me-2"></i> Yeni Ürün
            </button>
          </div>
        }
      />

      {/* Filters */}
      <FilterBar
        filters={[
          {
            type: 'select',
            key: 'stockFilter',
            label: 'Stok',
            defaultValue: '',
            options: [
              { value: '', label: 'Tüm Stok' },
              { value: 'low', label: 'Düşük Stok (≤10)' }
            ]
          }
        ]}
        activeFilters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchPlaceholder="Ürün Ara..."
        showSearch={true}
        showClearButton={true}
      />

      {/* Bulk Actions */}
      {selectedProductIds.length > 0 && (
        <div className="card border-0 shadow-sm mb-4 position-sticky" style={{ top: '80px', zIndex: 1020 }}>
          <div className="card-body p-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
            <div className="d-flex align-items-center justify-content-between">
              <span className="text-muted small">
                <i className="fas fa-check-circle text-primary me-2"></i>
                {selectedProductIds.length} ürün seçildi
              </span>
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setSelectedProductIds([])}>
                  <i className="fas fa-times me-1"></i> Seçimi Temizle
                </button>
                <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-1"></i> Sil
                </button>
                <button className="btn btn-warning btn-sm" onClick={openDiscount}>
                  <i className="fas fa-percentage me-1"></i> İndirim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <DataTable
        tableId="products"
        columns={columns}
        data={products}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onRowClick={handleEdit}
        emptyState={{
          title: 'Ürün Bulunamadı',
          message: 'Gösterilecek ürün bulunmuyor.',
          icon: 'box-open'
        }}
      />

      {/* Modals */}
      <ShopifyImportModal
        isOpen={isShopifyOpen}
        onClose={closeShopify}
        onSuccess={() => {
          refresh();
        }}
      />

      <DiscountModal
        isOpen={isDiscountOpen}
        onClose={closeDiscount}
        selectedProducts={products.filter(p => selectedProductIds.includes(p.id))}
        onSuccess={() => {
          refresh();
          setSelectedProductIds([]);
          closeDiscount();
        }}
      />

      {/* Product Drawer */}
      <ProductModal
        isOpen={isProductDrawerOpen}
        onClose={closeProductDrawer}
        product={selectedProduct}
        onSuccess={() => handleCloseProductDrawer(true)}
      />

      {/* Confirmation Modals */}
      <ConfirmModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setProductToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Ürünü Sil"
        message="Bu ürünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        loading={deleteLoading}
      />

      <ConfirmModal
        show={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        title="Toplu Silme"
        message={`Seçili ${selectedProductIds.length} ürünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}

export default ProductManagement; 